/**
 * Reusable Excel import for hierarchy taxonomies (capability, process,
 * organisation, objective, initiative, etc.).
 * Columns: PCF ID, Hierarchy ID, Name, Element Description.
 * Builds parent-child from Hierarchy ID and creates entities via bulk API.
 * Import runs in the background; user can close the dialog and continue working.
 */

import React, { useState, useEffect } from "react";
import {
  createEntitiesBulkHierarchyBackground,
  getJobStatus,
} from "../services/api";
import { parseHierarchyExcel } from "../utils/hierarchyExcelImport";

const HierarchyExcelImport = ({
  entityType,
  idPrefix,
  entityLabel,
  entityLabelPlural,
  onSuccess,
  onClose,
}) => {
  const plural =
    entityLabelPlural ||
    (entityLabel
      ? entityLabel.endsWith("s")
        ? entityLabel
        : `${entityLabel}s`
      : "items");
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [importJobId, setImportJobId] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [pollingTimeoutId, setPollingTimeoutId] = useState(null);

  useEffect(() => {
    return () => {
      if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
    };
  }, [pollingTimeoutId]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);
    setParsed(null);
    setError("");
    setImportStatus(null);
    setImportJobId(null);
    if (!f) return;
    parseHierarchyExcel(f).then(({ rows, error: err }) => {
      if (err) {
        setError(err);
        setParsed(null);
        return;
      }
      setError("");
      setParsed({ rows });
    });
  };

  const pollHierarchyJobStatus = (jobId) => {
    const maxAttempts = 300;
    let attempts = 0;

    const poll = async () => {
      try {
        const job = await getJobStatus(jobId);
        setImportStatus(job);

        if (job.status === "completed") {
          let result = job.result || {};
          if (typeof result === "string") {
            try {
              result = JSON.parse(result);
            } catch (_) {
              result = {};
            }
          }
          const created = result.created ?? 0;
          const totalItems = result.total ?? 0;
          setImporting(false);
          setImportJobId(null);
          setPollingTimeoutId(null);
          if (created > 0) {
            onSuccess?.();
            onClose?.();
          } else {
            setError(
              totalItems > 0
                ? "No entities were created. Check that the entity type exists and names are not duplicates."
                : "No valid rows to import."
            );
          }
          return;
        }
        if (job.status === "failed") {
          setError(job.error_message || job.error || "Import failed");
          setImporting(false);
          setImportJobId(null);
          setPollingTimeoutId(null);
          return;
        }
        if (job.status === "running" || job.status === "pending") {
          attempts++;
          if (attempts < maxAttempts) {
            const timeoutId = setTimeout(poll, 1000);
            setPollingTimeoutId(timeoutId);
          } else {
            setError("Import is taking longer than expected. You can close this dialog and check back later.");
            setImporting(false);
            setImportJobId(null);
            setPollingTimeoutId(null);
          }
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          const timeoutId = setTimeout(poll, 1000);
          setPollingTimeoutId(timeoutId);
        } else {
          setError("Failed to check import status: " + (err.message || "Unknown error"));
          setImporting(false);
          setImportJobId(null);
          setPollingTimeoutId(null);
        }
      }
    };

    poll();
  };

  const handleImport = async () => {
    if (!parsed?.rows?.length) return;
    setImporting(true);
    setError("");
    setImportStatus(null);
    setImportJobId(null);
    const total = parsed.rows.length;
    setProgress({ current: 0, total });

    try {
      const result = await createEntitiesBulkHierarchyBackground(
        entityType,
        idPrefix,
        parsed.rows
      );

      if (result?.job_id) {
        setImportJobId(result.job_id);
        setImportStatus({
          status: "queued",
          message: result.message || "Import is running in the background. You can close this dialog and continue working.",
        });
        setImporting(false);
        pollHierarchyJobStatus(result.job_id);
      } else {
        setError("Failed to start background import (no job ID returned).");
        setImporting(false);
      }
    } catch (e) {
      const msg = e.response?.data?.detail ?? e.message ?? "Import failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setImporting(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const isJobRunning =
    importStatus &&
    (importStatus.status === "queued" ||
      importStatus.status === "pending" ||
      importStatus.status === "running");

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">
            Import {plural} from Excel
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            File must have columns: <strong>Hierarchy ID</strong>, <strong>Name</strong>, <strong>Description</strong>.
            Parent is derived from hierarchy ID (e.g. 9.0 = root, 9.1 = child of 9.0, 9.1.1 = child of 9.1).
          </p>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Select Excel file (.xlsx, .xls)
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={importing}
              className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:font-medium hover:file:bg-purple-700"
            />
          </div>
          {error && (
            <div className="p-3 rounded bg-red-900/30 border border-red-700 text-red-300 text-sm">
              {error}
            </div>
          )}

          {importStatus && (
            <div className="p-3 rounded bg-gray-700/50 border border-gray-600 space-y-2">
              <h4 className="text-sm font-medium text-white">Import status</h4>
              {importStatus.status === "queued" && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500" />
                    <span className="text-sm text-green-400">Import queued</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    {importStatus.message || "Import is running in the background."}
                  </p>
                  <p className="text-xs text-gray-500">
                    You can close this dialog; the import will continue in the background.
                  </p>
                </>
              )}
              {(importStatus.status === "pending" || importStatus.status === "running") && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500" />
                    <span className="text-sm text-gray-300">
                      {importStatus.status === "running" ? "Processing…" : "Waiting to process…"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    You can close this dialog; the import will continue in the background.
                  </p>
                </>
              )}
              {isJobRunning && (
                <div className="pt-2 border-t border-gray-600">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
                  >
                    Close and continue working
                  </button>
                </div>
              )}
            </div>
          )}

          {parsed?.rows?.length > 0 && (
            <>
              <div className="text-sm text-gray-300">
                <strong>{parsed.rows.length}</strong> {entityLabel}(s) found.
                Preview:
              </div>
              <div className="rounded border border-gray-600 overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-700/50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-gray-400 font-medium">
                        Hierarchy ID
                      </th>
                      <th className="px-2 py-1.5 text-gray-400 font-medium">
                        Name
                      </th>
                      <th className="px-2 py-1.5 text-gray-400 font-medium">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {parsed.rows.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-700">
                        <td className="px-2 py-1">{row.hierarchyId}</td>
                        <td className="px-2 py-1 truncate max-w-[200px]">
                          {row.name}
                        </td>
                        <td className="px-2 py-1 truncate max-w-[180px] text-gray-400">
                          {row.elementDescription || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 10 && (
                  <div className="px-2 py-1 bg-gray-700/30 text-gray-500 text-xs">
                    ... and {parsed.rows.length - 10} more
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-gray-700 flex items-center justify-between gap-3">
          {importing && (
            <span className="text-sm text-gray-400">
              Starting import…
            </span>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              disabled={importing}
              className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={importing || !parsed?.rows?.length || !!importJobId}
              className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {importing ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Starting…
                </>
              ) : (
                "Import"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HierarchyExcelImport;
